import { onPopupResolverSettled, popupResolverReady, warmUpPopupResolver } from "./firebase";

/**
 * ENG-3625: the popup sign-in iframe's warm-up, as the pages call it.
 *
 * Kept out of lib/firebase.ts so a test that mocks that module without these exports still renders
 * App; every call is caught for the same reason, and because a warm-up must never break a page.
 */

/** Starts the warm-up now. It is idempotent, and does nothing once the iframe has loaded. */
export function warmUpPopupResolverNow(): void {
  try {
    warmUpPopupResolver();
  } catch {
    // Best effort: a popup call loads the iframe itself.
  }
}

/** Whether the popup sign-in iframe has loaded. False where that cannot be read. */
export function isPopupResolverReady(): boolean {
  try {
    return popupResolverReady() === true;
  } catch {
    return false;
  }
}

/**
 * Listens for each warm-up to finish (`true` loaded, `false` failed). Returns the unsubscribe, or null
 * where there is nothing to listen to, so a caller waiting on it can stop waiting.
 */
export function subscribePopupResolver(listener: (ready: boolean) => void): (() => void) | null {
  try {
    const unsubscribe = onPopupResolverSettled(listener);
    return typeof unsubscribe === "function" ? unsubscribe : null;
  } catch {
    return null;
  }
}

/**
 * Schedules the warm-up once the page has painted, when the main thread is next idle (at most a second
 * later), and returns a cancel for an effect cleanup. Safari has no `requestIdleCallback`, so there it
 * runs on the next task.
 */
export function schedulePopupResolverWarmUp(): () => void {
  const run = () => warmUpPopupResolverNow();
  // Typed as always present (lib.dom), which Safari is not.
  if (typeof window.requestIdleCallback === "function") {
    const id = window.requestIdleCallback(run, { timeout: 1000 });
    return () => window.cancelIdleCallback(id);
  }
  const id = window.setTimeout(run, 0);
  return () => window.clearTimeout(id);
}
